import { useGummiwormContext } from "@/context/GummiwormContext";
import { FC, useCallback, useLayoutEffect, useMemo, useRef, useState } from "react";
import type { Market, OrderbookDepth } from "@module-bindings/types";
import { assetIdKey, formatAssetLabel, formatAssetValue } from "@/lib/marketUtils";
import type { AssetId } from "@module-bindings/types";
import { TradingPaneState } from "./TradingPaneState";

type ViewMode = "both" | "bids" | "asks";

const FRAC_BITS = 60n;

/** Convert fixed-point u128 price to a display number. */
function priceToNumber(raw: bigint): number {
  const integer = Number(raw >> FRAC_BITS);
  const fractional = Number(raw & ((1n << FRAC_BITS) - 1n)) / Number(1n << FRAC_BITS);
  return integer + fractional;
}

interface PriceLevel {
  price: number;
  deltaAmount: number;
  quoteAmount: number;
  quoteTotal: number;
}

/** Format orderbook depth into price levels, sorted best-first. */
function buildLevels(depths: readonly OrderbookDepth[], side: "Bid" | "Ask"): PriceLevel[] {
  const byPrice = new Map<number, number>();
  for (const d of depths) {
    const amount = side == "Bid" ? d.bidAmount : d.askAmount;
    if (amount === 0n) continue;
    const p = priceToNumber(d.price);
    byPrice.set(p, Number(amount));
  }

  // Sort: bids high→low, asks low→high
  const sorted = [...byPrice.entries()].sort((a, b) =>
    side === "Bid" ? b[0] - a[0] : a[0] - b[0]
  );

  let cumulative = 0;
  let previousAmount = 0;
  return sorted.map(([price, quoteAmount], index) => {
    cumulative += quoteAmount;
    const deltaAmount = index === 0 ? quoteAmount : quoteAmount - previousAmount;
    previousAmount = quoteAmount;
    return { price, deltaAmount, quoteAmount, quoteTotal: cumulative };
  });
}

interface OrderBookProps {
  selectedMarket: Market | null;
  onSelectPrice?: (price: number) => void;
}

export const OrderBook: FC<OrderBookProps> = ({ selectedMarket, onSelectPrice }) => {
  const { ledgerView } = useGummiwormContext();
  const [viewMode, setViewMode] = useState<ViewMode>("both");
  const [autoCenterSpread, setAutoCenterSpread] = useState(true);
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const spreadRowRef = useRef<HTMLDivElement | null>(null);
  const centeredSignatureRef = useRef<string>("");
  const isProgrammaticScrollRef = useRef(false);

  // Query by the selected market
  const pairKey = useMemo(() => {
    if (!selectedMarket) return '';
    const baseKey = assetIdKey(selectedMarket.tradingPair.base);
    const quoteKey = assetIdKey(selectedMarket.tradingPair.quote);
    return `${baseKey}/${quoteKey}`;
  }, [selectedMarket]);
  const depths = useMemo(() => {
    if (!ledgerView) return [];
    if (!ledgerView.depths[pairKey]) return [];
    return ledgerView.depths[pairKey];
  }, [ledgerView?.depths, pairKey]);

  const askLevels = useMemo(() => buildLevels(depths, "Ask"), [depths]);
  const bidLevels = useMemo(() => buildLevels(depths, "Bid"), [depths]);

  const quoteLabel = selectedMarket ? formatAssetLabel(selectedMarket.tradingPair.quote) : "Quote";

  const showAsks = viewMode !== "bids";
  const showBids = viewMode !== "asks";
  const showSpread = viewMode === "both";

  // Spread
  const bestAsk = askLevels[0]?.price;
  const bestBid = bidLevels[0]?.price;
  const spread = bestAsk != null && bestBid != null ? bestAsk - bestBid : null;
  const spreadPct = spread != null && bestAsk > 0
    ? ((spread / bestAsk) * 100).toFixed(2)
    : null;

  const getSpreadCenteredScrollTop = useCallback(() => {
    const container = scrollContainerRef.current;
    const spreadRow = spreadRowRef.current;
    if (!container || !spreadRow) return null;

    const containerRect = container.getBoundingClientRect();
    const spreadRect = spreadRow.getBoundingClientRect();
    const spreadTopWithinContainer =
      container.scrollTop + (spreadRect.top - containerRect.top);
    const targetTop =
      spreadTopWithinContainer - (container.clientHeight - spreadRect.height) / 2;
    const maxTop = Math.max(0, container.scrollHeight - container.clientHeight);
    return Math.max(0, Math.min(targetTop, maxTop));
  }, []);

  const setProgrammaticScrollTop = useCallback((top: number) => {
    const container = scrollContainerRef.current;
    if (!container) return;

    isProgrammaticScrollRef.current = true;
    container.scrollTop = top;

    requestAnimationFrame(() => {
      isProgrammaticScrollRef.current = false;
    });
  }, []);

  const resetScrollPosition = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    if (showSpread) {
      setAutoCenterSpread(true);
      const targetTop = getSpreadCenteredScrollTop();
      if (targetTop == null) return;
      setProgrammaticScrollTop(targetTop);
      return;
    }

    setAutoCenterSpread(false);
    setProgrammaticScrollTop(viewMode === "asks" ? container.scrollHeight : 0);
  }, [getSpreadCenteredScrollTop, setProgrammaticScrollTop, showSpread, viewMode]);

  useLayoutEffect(() => {
    const signature = `${pairKey}:${viewMode}`;
    if (centeredSignatureRef.current === signature) return;
    centeredSignatureRef.current = signature;
    setAutoCenterSpread(showSpread);
    resetScrollPosition();
  }, [pairKey, viewMode, resetScrollPosition, showSpread]);

  useLayoutEffect(() => {
    if (!showSpread || !autoCenterSpread) return;
    const targetTop = getSpreadCenteredScrollTop();
    if (targetTop == null) return;
    setProgrammaticScrollTop(targetTop);
  }, [
    askLevels.length,
    bidLevels.length,
    bestAsk,
    bestBid,
    autoCenterSpread,
    getSpreadCenteredScrollTop,
    setProgrammaticScrollTop,
    showSpread,
  ]);

  const handleScroll = useCallback(() => {
    if (!showSpread || isProgrammaticScrollRef.current) return;
    const container = scrollContainerRef.current;
    const spreadRow = spreadRowRef.current;
    if (!container || !spreadRow) return;

    const containerRect = container.getBoundingClientRect();
    const spreadRect = spreadRow.getBoundingClientRect();
    const spreadMidpoint = spreadRect.top - containerRect.top + spreadRect.height / 2;
    const containerMidpoint = container.clientHeight / 2;

    if (Math.abs(spreadMidpoint - containerMidpoint) > 12) {
      setAutoCenterSpread(false);
    }
  }, [showSpread]);

  const maxAskTotal = askLevels.length > 0 ? askLevels[askLevels.length - 1].quoteTotal : 1;
  const maxBidTotal = bidLevels.length > 0 ? bidLevels[bidLevels.length - 1].quoteTotal : 1;
  const maxAskDelta = askLevels.reduce((max, level) => Math.max(max, Math.abs(level.deltaAmount)), 1);
  const maxBidDelta = bidLevels.reduce((max, level) => Math.max(max, Math.abs(level.deltaAmount)), 1);

  // For "both" mode, show asks reversed so best ask is at the bottom (near spread)
  const displayAsks = showSpread ? [...askLevels].reverse() : askLevels;

  if (!selectedMarket) {
    return (
      <div className="sr-panel flex h-full min-h-[20rem] flex-col">
        <TradingPaneState
          eyebrow="Depth"
          title="No market selected"
          message="Pick a trading pair to inspect the order book."
        />
      </div>
    );
  }

  if (askLevels.length === 0 && bidLevels.length === 0) {
    return (
      <div className="sr-panel flex h-full min-h-[20rem] flex-col">
        <div className="sr-panel-header">
          <h3 className="text-[11px] font-medium uppercase tracking-wider text-sr-neutral-300">
            Order Book
          </h3>
        </div>
        <TradingPaneState
          eyebrow="Depth"
          title="No liquidity yet"
          message="This market has no active bids or asks. Check back after the first orders land."
        />
      </div>
    );
  }

  return (
    <section className="sr-panel flex h-full flex-col" aria-label="Order book">
      {/* Header */}
      <div className="sr-panel-header border-none pb-2">
        <h3 className="text-[11px] font-medium text-sr-neutral-300 uppercase tracking-wider">
          Order Book
        </h3>
        <div className="flex items-center gap-2">
          {showSpread && !autoCenterSpread && (
            <button
              type="button"
              onClick={resetScrollPosition}
              className="sr-focus-ring rounded-md border border-sr-border-subtle px-2 py-1 text-[10px] font-medium uppercase tracking-wider text-sr-neutral-300 transition-colors hover:bg-white/5 hover:text-white"
            >
              Reset
            </button>
          )}
          <div className="flex items-center gap-0.5" role="toolbar" aria-label="Order book view mode">
            <button
              onClick={() => setViewMode("both")}
              className="sr-focus-ring rounded p-1"
              title="Both"
              aria-pressed={viewMode === "both"}
              aria-label="Show asks and bids"
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <rect x="2" y="2" width="5" height="5" rx="1" fill={viewMode === "both" ? "var(--color-sr-salmon)" : "var(--color-sr-neutral-700)"} />
                <rect x="9" y="2" width="5" height="5" rx="1" fill={viewMode === "both" ? "var(--color-sr-salmon)" : "var(--color-sr-neutral-700)"} />
                <rect x="2" y="9" width="5" height="5" rx="1" fill={viewMode === "both" ? "var(--color-sr-mint)" : "var(--color-sr-neutral-700)"} />
                <rect x="9" y="9" width="5" height="5" rx="1" fill={viewMode === "both" ? "var(--color-sr-mint)" : "var(--color-sr-neutral-700)"} />
              </svg>
            </button>
            <button
              onClick={() => setViewMode("bids")}
              className="sr-focus-ring rounded p-1"
              title="Bids"
              aria-pressed={viewMode === "bids"}
              aria-label="Show bids only"
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <rect x="2" y="2" width="5" height="5" rx="1" fill={viewMode === "bids" ? "var(--color-sr-mint)" : "var(--color-sr-neutral-700)"} />
                <rect x="9" y="2" width="5" height="5" rx="1" fill={viewMode === "bids" ? "var(--color-sr-mint)" : "var(--color-sr-neutral-700)"} />
                <rect x="2" y="9" width="5" height="5" rx="1" fill={viewMode === "bids" ? "var(--color-sr-mint)" : "var(--color-sr-neutral-700)"} />
                <rect x="9" y="9" width="5" height="5" rx="1" fill={viewMode === "bids" ? "var(--color-sr-mint)" : "var(--color-sr-neutral-700)"} />
              </svg>
            </button>
            <button
              onClick={() => setViewMode("asks")}
              className="sr-focus-ring rounded p-1"
              title="Asks"
              aria-pressed={viewMode === "asks"}
              aria-label="Show asks only"
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <rect x="2" y="2" width="5" height="5" rx="1" fill={viewMode === "asks" ? "var(--color-sr-salmon)" : "var(--color-sr-neutral-700)"} />
                <rect x="9" y="2" width="5" height="5" rx="1" fill={viewMode === "asks" ? "var(--color-sr-salmon)" : "var(--color-sr-neutral-700)"} />
                <rect x="2" y="9" width="5" height="5" rx="1" fill={viewMode === "asks" ? "var(--color-sr-salmon)" : "var(--color-sr-neutral-700)"} />
                <rect x="9" y="9" width="5" height="5" rx="1" fill={viewMode === "asks" ? "var(--color-sr-salmon)" : "var(--color-sr-neutral-700)"} />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Column headers */}
      <div className="grid grid-cols-[1.15fr_0.65fr_0.7fr_0.7fr] items-end text-[9px] text-sr-neutral-500 uppercase tracking-wider px-2 pb-1">
        <span className="px-2">Price</span>
        <span className="text-right">Delta</span>
        <span className="text-right">Size</span>
        <span className="text-right px-2">Sum</span>
      </div>

      <div
        ref={scrollContainerRef}
        className="min-h-0 flex flex-1 flex-col overflow-y-auto pb-5"
        onScroll={handleScroll}
        style={{ overflowAnchor: "none" }}
      >
        {/* Asks */}
        {showAsks && (
          <div
            className={showSpread ? "flex min-h-0 flex-1 flex-col justify-end" : "flex flex-col"}
          >
            {displayAsks.length > 0 ? displayAsks.map((level, i) => (
              <Row key={`ask-${i}`} level={level} side="ask" maxTotal={maxAskTotal} maxDelta={maxAskDelta} quoteAsset={selectedMarket?.tradingPair.quote ?? null} onSelect={onSelectPrice} />
            )) : (
              <div className="flex-1 flex items-end justify-center pb-2">
                <span className="text-xs text-sr-neutral-700">No asks</span>
              </div>
            )}
          </div>
        )}

        {/* Spread */}
        {showSpread && (
          <div
            ref={spreadRowRef}
            className="flex h-7 shrink-0 items-center justify-between border-y border-sr-border-subtle/50 px-3 text-[10px]"
          >
            <span className="text-sr-neutral-500">Spread</span>
            {spread != null ? (
              <div className="flex items-center gap-2">
                <span className="text-sr-neutral-300 tabular-nums">{spreadPct}%</span>
                <span className="text-white tabular-nums font-display">{spread.toFixed(6)} {quoteLabel}</span>
              </div>
            ) : (
              <span className="text-sr-neutral-700">—</span>
            )}
          </div>
        )}

        {/* Bids */}
        {showBids && (
          <div className={showSpread ? "flex min-h-0 flex-1 flex-col" : "flex flex-col"}>
            {bidLevels.length > 0 ? bidLevels.map((level, i) => (
              <Row key={`bid-${i}`} level={level} side="bid" maxTotal={maxBidTotal} maxDelta={maxBidDelta} quoteAsset={selectedMarket?.tradingPair.quote ?? null} onSelect={onSelectPrice} />
            )) : (
              <div className="flex-1 flex items-start justify-center pt-2">
                <span className="text-xs text-sr-neutral-700">No bids</span>
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  );
};

const Row: FC<{
  level: PriceLevel;
  side: "ask" | "bid";
  maxTotal: number;
  maxDelta: number;
  quoteAsset: AssetId | null;
  onSelect?: (price: number) => void;
}> = ({ level, side, maxTotal, maxDelta, quoteAsset, onSelect }) => {
  const totalPct = maxTotal > 0 ? (level.quoteTotal / maxTotal) * 100 : 0;
  const deltaPct = maxDelta > 0 ? (Math.abs(level.deltaAmount) / maxDelta) * 100 : 0;
  const isAsk = side === "ask";
  const formatQuote = (value: number) => {
    if (!quoteAsset) {
      return value.toLocaleString(undefined, { maximumFractionDigits: 2 });
    }

    return formatAssetValue(quoteAsset, BigInt(Math.round(value)));
  };
  const formatSignedQuote = (value: number) => {
    const magnitude = formatQuote(Math.abs(value));
    if (value > 0) return `+${magnitude}`;
    if (value < 0) return `-${magnitude}`;
    return magnitude;
  };

  return (
    <div
      className="relative grid h-7 shrink-0 grid-cols-[1.15fr_0.65fr_0.7fr_0.7fr] items-center px-2 text-[10px] hover:bg-white/5 cursor-pointer"
      onClick={() => onSelect?.(level.price)}
    >
      <div
        className={`absolute inset-y-0 right-0 pointer-events-none ${isAsk ? "bg-sr-salmon/20" : "bg-sr-mint/15"}`}
        style={{ width: `${totalPct}%` }}
      />
      <div
        className={`absolute inset-y-0 right-0 pointer-events-none ${isAsk ? "bg-sr-salmon/70" : "bg-sr-mint/65"}`}
        style={{ width: `${deltaPct}%` }}
      />
      <span className={`relative tabular-nums font-display px-1 ${isAsk ? "text-sr-salmon" : "text-sr-mint"}`}>
        {level.price.toFixed(6)}
      </span>
      <span className={`relative text-right tabular-nums ${level.deltaAmount < 0 ? "text-sr-salmon" : level.deltaAmount > 0 ? "text-sr-mint" : "text-sr-neutral-500"}`}>
        {formatSignedQuote(level.deltaAmount)}
      </span>
      <span className="relative text-right text-white tabular-nums">
        {formatQuote(level.quoteAmount)}
      </span>
      <span className="relative text-right text-white tabular-nums px-1">
        {formatQuote(level.quoteTotal)}
      </span>
    </div>
  );
};
